// modal-kit.js  — Minimal <dialog> helper (ESM / no CSS / collision-safe)
// API: modalKitInit(options), modalKitShow(target), modalKitHide(target), modalKitDispose()

const cfg = {
  dialogSelector: "dialog.modal",
  openSelector: ".js-modal-open",
  closeSelector: ".js-modal-close",
  innerSelector: ".modal__inner",
  defaultTarget: "#recruitModal",
  backdropClose: true,
  lockScroll: true,
  focusReturn: true,
  debug: false,
  // フェード時間のフォールバック（CSSのtransitionendが来なかった時用）
  fallbackMs: 300,
};

const st = {
  lastFocus: null,
  scrollY: 0,
  boundClick: null,
  dialogs: new Set(), // このセッションで開いたdialogを記録
};

const log = (...a) => cfg.debug && console.log("[ModalKit]", ...a);
const qs = (s, c = document) => c.querySelector(s);

/* util */
function resolve(target) {
  if (!target) target = cfg.defaultTarget;
  if (typeof target === "string") {
    return target.startsWith("#") || target.startsWith(".")
      ? qs(target)
      : qs("#" + target);
  }
  return target;
}
function lockScroll() {
  if (!cfg.lockScroll) return;
  st.scrollY = window.scrollY || 0;
  document.documentElement.style.scrollBehavior = "auto";
  Object.assign(document.body.style, {
    position: "fixed",
    top: `-${st.scrollY}px`,
    left: "0",
    right: "0",
    width: "100%",
  });
}
function unlockScroll() {
  if (!cfg.lockScroll) return;
  Object.assign(document.body.style, {
    position: "",
    top: "",
    left: "",
    right: "",
    width: "",
  });
  window.scrollTo(0, st.scrollY);
  document.documentElement.style.scrollBehavior = "";
}

function cleanupAfterClose(dlg) {
  dlg.classList.remove("is-open");
  if (dlg.__mk_backdrop__) {
    dlg.removeEventListener("click", dlg.__mk_backdrop__);
    dlg.__mk_backdrop__ = null;
  }
  unlockScroll();
  if (
    cfg.focusReturn &&
    st.lastFocus &&
    typeof st.lastFocus.focus === "function"
  ) {
    try {
      st.lastFocus.focus();
    } catch (_) {}
  }
}

export function modalKitShow(target) {
  const dlg = resolve(target);
  if (!dlg) return;

  st.lastFocus = document.activeElement;

  // open（<dialog>未対応環境は簡易フォールバック）
  if (typeof dlg.showModal === "function") dlg.showModal();
  else {
    dlg.setAttribute("open", "");
    dlg.setAttribute("role", "dialog");
    dlg.setAttribute("aria-modal", "true");
  }

  lockScroll();

  // フェードイン発火：次フレームで is-open 付与
  requestAnimationFrame(() => dlg.classList.add("is-open"));

  // 背景クリックで閉じる（.modal__inner の枠外クリックを検知）
  const inner = qs(cfg.innerSelector, dlg) || dlg.firstElementChild || dlg;
  dlg.__mk_backdrop__ = (e) => {
    if (!cfg.backdropClose) return;
    const r = inner.getBoundingClientRect();
    const x = e.clientX,
      y = e.clientY;
    if (x < r.left || x > r.right || y < r.top || y > r.bottom)
      modalKitHide(dlg);
  };
  dlg.addEventListener("click", dlg.__mk_backdrop__);

  // closeイベント（プログラム/フォームで閉じた時の後処理）
  const onClose = () => {
    dlg.removeEventListener("close", onClose);
    cleanupAfterClose(dlg);
  };
  dlg.addEventListener("close", onClose);

  // ESCでの閉じ処理をアニメ付きに（cancelはデフォでcloseするので横取り）
  if (!dlg.__mk_cancel__) {
    dlg.__mk_cancel__ = (e) => {
      e.preventDefault();
      modalKitHide(dlg);
    };
    dlg.addEventListener("cancel", dlg.__mk_cancel__);
  }

  st.dialogs.add(dlg);
  log("open", dlg.id || dlg);
}

export function modalKitHide(target) {
  const dlg = resolve(target) || [...st.dialogs].find((d) => d.open);
  if (!dlg) return;

  // 既に閉じ中なら何もしない
  if (
    !dlg.classList.contains("is-open") &&
    !dlg.open &&
    !dlg.hasAttribute("open")
  )
    return;

  // フェードアウト：is-open を外して、transitionend で実閉じ
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    if (dlg.open && typeof dlg.close === "function")
      dlg.close(); // close() → onClose で後処理
    else {
      dlg.removeAttribute("open");
      cleanupAfterClose(dlg);
    } // フォールバック
  };

  const onEnd = (e) => {
    if (e.target !== dlg) return; // 子要素のtransitionは無視
    dlg.removeEventListener("transitionend", onEnd);
    finish();
  };

  dlg.addEventListener("transitionend", onEnd);
  dlg.classList.remove("is-open");

  // 念のためのフォールバック（transitionend来ない環境向け）
  setTimeout(() => {
    dlg.removeEventListener("transitionend", onEnd);
    finish();
  }, cfg.fallbackMs);

  log("hide", dlg.id || dlg);
}

export function modalKitInit(options = {}) {
  Object.assign(cfg, options);
  if (st.boundClick) return; // 二重初期化ガード

  st.boundClick = (e) => {
    // 開く
    const openBtn = e.target.closest(cfg.openSelector);
    if (openBtn) {
      e.preventDefault(); // aタグ等でもページ遷移させない
      const sel = openBtn.getAttribute("data-modal") || cfg.defaultTarget;
      modalKitShow(sel);
      return;
    }
    // 閉じる
    const closeBtn = e.target.closest(cfg.closeSelector);
    if (closeBtn) {
      e.preventDefault(); // form method="dialog" と二重でも安全
      const dlg = closeBtn.closest("dialog");
      if (dlg) modalKitHide(dlg);
      return;
    }
  };

  document.addEventListener("click", st.boundClick);
  log("initialized", { ...cfg });
}

export function modalKitDispose() {
  if (st.boundClick) document.removeEventListener("click", st.boundClick);
  st.boundClick = null;
  st.dialogs.forEach((dlg) => {
    if (dlg.__mk_backdrop__)
      dlg.removeEventListener("click", dlg.__mk_backdrop__);
    if (dlg.__mk_cancel__) dlg.removeEventListener("cancel", dlg.__mk_cancel__);
    dlg.__mk_backdrop__ = dlg.__mk_cancel__ = null;
  });
  st.dialogs.clear();
  log("disposed");
}

export default { modalKitInit, modalKitShow, modalKitHide, modalKitDispose };
